import React from "react";
import { Phone, Clock, X } from "lucide-react";
import { useFollowupNotification } from "../../context/FollowupNotificationContext";
import { initialsOf } from "../../pages/Agent/Dashboard2/utils";
import DraggablePopup from "./DraggablePopup";

const POPUP_WIDTH = 320; // matches the card's max-w-sm

// Rendered above everything else on the page (not scoped to any one route) so
// a due follow-up grabs the agent's attention no matter where they are — but
// draggable, not a blocking modal, since it can come up mid-call (e.g. while
// a campaign call's feedback form is open) and shouldn't force the agent to
// dismiss (and lose) it just to get it out of the way.
const FollowupCallToast = () => {
  const {
    pendingCalls,
    nowTick,
    notifyLeadSec,
    cancelPendingCall,
    callPendingCallNow,
    hasWebRtc,
    isOnCall,
  } = useFollowupNotification();

  if (pendingCalls.length === 0) return null;

  const [active, ...queued] = pendingCalls;
  const secondsLeft = Math.max(0, Math.ceil((active.dueAt - nowTick) / 1000));
  const progress = notifyLeadSec > 0 ? Math.min(1, Math.max(0, 1 - secondsLeft / notifyLeadSec)) : 1;
  const ringDeg = Math.round(progress * 360);

  return (
    <DraggablePopup
      key={active.key}
      initialPos={{
        x: (window.innerWidth - POPUP_WIDTH) / 2,
        y: 24,
      }}
    >
      <div className="relative w-[320px] rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 p-7">
        <button
          type="button"
          onClick={() => cancelPendingCall(active.key)}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          title="Dismiss"
        >
          <X size={18} />
        </button>

        <div className="flex flex-col items-center text-center space-y-3">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-blue-600">
            <Clock size={13} />
            Upcoming follow-up call
          </div>

          <div
            className="relative w-24 h-24 rounded-full flex items-center justify-center"
            style={{
              background: active.queued
                ? "#E9F0FE"
                : `conic-gradient(#2F6FED ${ringDeg}deg, #E9F0FE ${ringDeg}deg)`,
            }}
          >
            <div className="w-[84px] h-[84px] rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-2xl font-semibold shadow-lg">
              {initialsOf(active.customer_name) || <Phone size={30} />}
            </div>
          </div>

          <div>
            <div className="text-lg font-semibold text-gray-800">
              {active.customer_name || "Unknown customer"}
            </div>
            <div className="text-sm text-gray-500">{active.customer_number}</div>
          </div>

          <div
            className={`text-sm font-medium ${
              active.queued ? "text-amber-600" : "text-blue-600"
            }`}
          >
            {active.queued ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                Waiting for current call to end…
              </span>
            ) : (
              `Calling in ${secondsLeft}s…`
            )}
          </div>

          <div className="flex gap-3 pt-2 w-full">
            <button
              type="button"
              onClick={() => cancelPendingCall(active.key)}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {hasWebRtc && !active.queued && (
              <button
                type="button"
                onClick={() => callPendingCallNow(active.key)}
                disabled={isOnCall}
                className="flex-1 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors"
              >
                <Phone size={15} />
                Call now
              </button>
            )}
          </div>

          {queued.length > 0 && (
            <div className="text-xs font-medium text-gray-400 pt-1">
              +{queued.length} more follow-up{queued.length > 1 ? "s" : ""} queued
            </div>
          )}
        </div>
      </div>
    </DraggablePopup>
  );
};

export default FollowupCallToast;
